// Runs the graphics quality at run time (no three.js here; Game.ApplyQuality does the drawing side):
//   - follows the setting: whenever the level to draw with changes (the player picks Low or High, or Auto finishes
//     measuring), onChange(level) is called once;
//   - on Auto, until a level has been measured on this browser, feeds every rendered frame to a FrameRateProbe and
//     stores the result (settings.detectedQuality), which in turn switches the level if it differs.
// Not while nothing is drawn (?test=1&norender=1): the frame rate then says nothing about drawing.
import {PlayerSettings, Settings} from "./settings";
import {FrameRateProbe, QualityLevel, effectiveQuality, needsProbe} from "./quality";

type SettingsStore = Pick<typeof PlayerSettings, "get" | "set" | "subscribe">;

export class QualityController {
    level: QualityLevel;
    probe: FrameRateProbe | null = null;
    /** the last measured frame rate (for the settings panel and the logs) */
    measuredFps: number | null = null;
    private unsubscribe: (() => void) | null = null;

    constructor(private onChange: (level: QualityLevel) => void, private canProbe = true,
                private settings: SettingsStore = PlayerSettings,
                private makeProbe: () => FrameRateProbe = () => new FrameRateProbe()) {
        this.level = effectiveQuality(settings.get());
        this.unsubscribe = settings.subscribe(s => this.follow(s));
    }

    private follow(s: Settings) {
        const level = effectiveQuality(s);
        if (level === this.level) return;
        this.level = level;
        this.onChange(level);
    }

    /** Once per rendered frame, with its timestamp (performance.now()). */
    frame(t: number) {
        if (!this.canProbe || !needsProbe(this.settings.get())) return;
        if (!this.probe || this.probe.finished) this.probe = this.makeProbe();
        const result = this.probe.frame(t);
        if (result) {
            this.measuredFps = this.probe.fps;
            console.info(`graphics quality: auto measured ${this.probe.fps.toFixed(1)} fps, using ${result}`);
            this.settings.set({detectedQuality: result});
        }
    }

    dispose() {
        if (this.unsubscribe) this.unsubscribe();
        this.unsubscribe = null;
    }
}

/**
 * First visit from a phone-like device (inputMode.ts isPhoneLike: a touch screen with a coarse or non-hover primary
 * pointer): Auto lands on Low straight away instead of measuring the frame rate at High first (mobile GPUs, battery,
 * heat). It is stored like a measurement (settings.detectedQuality), so Settings shows "Auto chose Low for this device"
 * and the player can still pick High; choosing Auto again measures as usual. Nothing happens when the player already
 * chose Low or High, when Auto already measured, or when autoAllowed is false (?test=1 without &autoquality=1, where Auto
 * stays at High for the e2e timing). Returns true when it chose Low.
 */
export function preferLowOnPhone(settings: SettingsStore, phoneLike: boolean, autoAllowed: boolean): boolean {
    if (!phoneLike || !autoAllowed || !needsProbe(settings.get())) return false;
    console.info("graphics quality: phone-like device, Auto starts at low");
    settings.set({detectedQuality: "low"});
    return true;
}
